import { useEffect, useState } from 'react'
import { callContract } from '../contractClient'
import { contracts } from '../../../../shared/features/platform.contracts'
import { contracts as meContracts } from '../../../../shared/features/me.contracts'
import { useToast } from './ToastProvider'

/**
 * Sticky banner shown for the lifetime of an impersonation session.
 *
 * Renders nothing when sessionStorage has no `impersonationToken`.
 * On mount, fetches `/api/users/me` once to learn who's being
 * impersonated (and which admin is doing it). On the End button or on
 * an `impersonation:ended` window event (fired by the apiClient
 * interceptor when admin returns expired/unknown), clears the token
 * and closes the tab if it was opened via `window.open` — otherwise
 * navigates to `/` with a toast.
 */
export default function ImpersonationBanner() {
  const [identities, setIdentities] = useState(null)
  const [ending, setEnding] = useState(false)
  const toast = useToast()
  const hasToken = typeof window !== 'undefined' && !!window.sessionStorage?.getItem('impersonationToken')

  useEffect(() => {
    if (!hasToken) return
    callContract(meContracts.usersMe)
      .then((res) => {
        const block = res.data?.user?.impersonation
        if (block) {
          setIdentities({
            user_email: res.data.user.email,
            admin_email: block.admin_email,
          })
        }
      })
      .catch(() => {
        // 401 here is handled by the apiClient interceptor; nothing
        // useful to do locally.
      })
  }, [hasToken])

  useEffect(() => {
    if (!hasToken) return
    const onEnded = () => {
      toast.error('Impersonation session ended — close this tab.')
    }
    window.addEventListener('impersonation:ended', onEnded)
    return () => window.removeEventListener('impersonation:ended', onEnded)
  }, [hasToken, toast])

  if (!hasToken) return null

  const endSession = async () => {
    setEnding(true)
    try {
      await callContract(contracts.endImpersonation)
    } catch {
      // Admin endpoint is idempotent; treat any failure as "ended" so
      // the operator can close the tab and re-launch if they need to.
    } finally {
      sessionStorage.removeItem('impersonationToken')
      if (window.opener) {
        window.close()
      } else {
        toast.success('Impersonation ended.')
        window.location.replace('/')
      }
    }
  }

  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 1080,
        /* deliberately loud: solid warn fill with its own dark ink (AA) */
        backgroundColor: 'var(--amber-9)',
        color: 'var(--amber-12)',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 16,
        fontSize: 14,
        borderBottom: '1px solid var(--amber-11)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <span style={{ fontWeight: 500 }}>⚠ VIEWING AS</span>
        <span style={{ fontFamily: 'monospace' }}>
          {identities?.user_email || 'loading…'}
        </span>
        {identities?.admin_email && (
          <>
            <span style={{ opacity: 0.85 }}>·</span>
            <span style={{ opacity: 0.85 }}>
              admin: <span style={{ fontFamily: 'monospace' }}>{identities.admin_email}</span>
            </span>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={endSession}
        disabled={ending}
        style={{
          backgroundColor: 'transparent',
          color: 'var(--amber-12)',
          border: '1px solid var(--amber-12)',
          borderRadius: 'var(--app-radius)',
          padding: '4px 12px',
          fontSize: 13,
          cursor: ending ? 'wait' : 'pointer',
        }}
      >
        {ending ? 'Ending…' : 'End Impersonation'}
      </button>
    </div>
  )
}
